import { CopyIcon } from '../../assets/images';
import { NudosButton, NudosHoverText, NudosLinkTypeButton } from '../NudosComponents';
import { formatRoundedUpNumber } from '../../utils/formatNumbers';
import { PAYMENT_TYPES } from '../../types/orders';
import { INudosTransfDetailsData } from '../../types/paymentDetails';
import './PaymentDetailsModal.scss';

/**
 * @param {Object} props - The component props.
 * @param {INudosTransfDetailsData} props.paymentDetailsModalData - The special data containing bank account information and payment details.
 */

const PaymentDetailsModalContent = ({
  paymentDetailsModalData,
  closeModalCallback
}: {
  paymentDetailsModalData?: INudosTransfDetailsData;
  closeModalCallback: () => void;
}) => {
  const noDataText = 'Sin datos';
  const { TRANSFER_PEN, TRANSFER_ARS, TRANSFER_BRL } = PAYMENT_TYPES;

  const {
    bankAccountInfo,
    currency,
    orderId,
    paymentMethod,
    paymentType,
    totalUsd,
    total,
    orderTotal,
    currencyCode,
    quoteDocumentUrl,
    manualQuote
  } = paymentDetailsModalData || {};

  const {
    accountNumber,
    beneficiary,
    beneficiaryAddress,
    cbu,
    cuit,
    clabe,
    beneficiaryBank,
    reference,
    accountType,
    routingNumber,
    bankAddress,
    accountHolderName,
    pixKey,
    cdf,
    cpf
  } = bankAccountInfo || {};

  const paymentMethodData = paymentMethod || paymentType;
  const beneficiaryCountryIsPeru = beneficiary?.toLowerCase().includes('peru') && orderId;
  const bankName = bankAccountInfo?.bank || noDataText;
  const beneficiaryValue = beneficiary || reference || noDataText;

  const displayRUT = bankAccountInfo?.rut;
  const displayCCI = bankAccountInfo?.cci;
  const displaySwift = bankAccountInfo?.swift;

  const transferIsARS = paymentMethodData === TRANSFER_ARS;
  const displayARSLayout = transferIsARS || currency === 'USD';
  const paymentInPenCurrency = currency === TRANSFER_PEN;
  const transerIsBRL = paymentMethodData === TRANSFER_BRL;
  const currencySymbol = paymentInPenCurrency ? 'S/' : '$';
  const countriesToDisplayPrinceInUSD = beneficiaryCountryIsPeru || displayARSLayout;
  const beneficiaryOrReferenceTitle = reference ? 'Referencia' : transerIsBRL ? 'Nombre completo' : 'Beneficiario';

  const valueToPay = (countriesToDisplayPrinceInUSD ? totalUsd : total) || orderTotal;
  const transferValue = valueToPay ? formatRoundedUpNumber(Number(valueToPay), 2) : noDataText;
  const transferCurrencyCode = (orderId ? displayARSLayout : undefined)
    ? 'USD'
    : currency || currencyCode || noDataText;

  const transferValueText = `${currencySymbol} ${transferValue} ${transferCurrencyCode}`;

  const countryIsPeru = beneficiary?.toLowerCase().includes('peru');
  const showOthersBankDataPeru = countryIsPeru && !!displaySwift;
  const charactersLimit = 24;
  const CPF = cdf || cpf || '';

  const formattedBankAddress = [
    bankAddress?.country || '',
    bankAddress?.city || '',
    bankAddress?.state || '',
    bankAddress?.line1 || '',
    bankAddress?.line2 || '',
    bankAddress?.postal_code || ''
  ].join(' ');

  return (
    <div className="nudosTransfDetailsContainer">
      <div className="nudosTransfDetails">
        {!!transerIsBRL && !!CPF && (
          <div className="informationField">
            <div className="title">Número de CPF</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(CPF))}>
              <NudosHoverText customClassName="subtitle" text={CPF} charactersLimit={charactersLimit} onlyIfTruncated />
              <div className="copyTextIcon">
                <CopyIcon className="copyTextIcon" stroke="#383838" />
              </div>
            </div>
          </div>
        )}
        {!!transerIsBRL && !!pixKey && (
          <div className="informationField">
            <div className="title">Clave PIX</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(pixKey))}>
              <NudosHoverText
                customClassName="subtitle"
                text={pixKey}
                charactersLimit={charactersLimit}
                onlyIfTruncated
              />
              <div className="copyTextIcon">
                <CopyIcon className="copyTextIcon" stroke="#383838" />
              </div>
            </div>
          </div>
        )}
        {!!accountNumber && (
          <div className="informationField">
            <div className="title">Numero de cuenta</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(accountNumber))}>
              {accountNumber}
              <div className="copyTextIcon">
                <CopyIcon className="copyTextIcon" stroke="#383838" />
              </div>
            </div>
          </div>
        )}
        {!!routingNumber && (
          <div className="informationField">
            <div className="title">ABA Número routing</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(routingNumber))}>
              {routingNumber}
              <div className="copyTextIcon">
                <CopyIcon className="copyTextIcon" stroke="#383838" />
              </div>
            </div>
          </div>
        )}
        {!!clabe && (
          <div className="informationField">
            <div className="title">CLABE</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(clabe))}>
              {clabe}
              <div className="copyTextIcon">
                <CopyIcon className="copyTextIcon" stroke="#383838" />
              </div>
            </div>
          </div>
        )}
        <div className="informationField">
          <div className="title">{beneficiaryOrReferenceTitle}</div>
          <NudosHoverText
            customClassName="subtitle"
            text={beneficiaryValue}
            charactersLimit={charactersLimit}
            onlyIfTruncated
          />
        </div>
        {!!displayRUT && (
          <div className="informationField">
            <div className="title">RUT</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(displayRUT))}>
              {displayRUT}
              <CopyIcon className="copyTextIcon" stroke="#383838" />
            </div>
          </div>
        )}
        {displayCCI && (
          <div className="informationField">
            <div className="title">CCI</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(displayCCI))}>
              <NudosHoverText
                customClassName="subtitle"
                text={displayCCI}
                charactersLimit={charactersLimit}
                onlyIfTruncated
              />
              <CopyIcon className="copyTextIcon" stroke="#383838" />
            </div>
          </div>
        )}
        {!!displaySwift && !displayCCI && (
          <div className="informationField">
            <div className="title">Código Swift</div>
            <NudosHoverText
              customClassName="subtitle"
              text={displaySwift}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {!!bankAddress && (
          <div className="informationField">
            <div className="title">Dirección del banco</div>
            <NudosHoverText
              customClassName="subtitle"
              text={formattedBankAddress}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        <div className="informationField">
          <div className="title">Banco</div>
          <NudosHoverText
            customClassName="subtitle"
            text={bankName}
            charactersLimit={charactersLimit}
            onlyIfTruncated
          />
        </div>
        {!!accountType && (
          <div className="informationField">
            <div className="title">Tipo de cuenta</div>
            <NudosHoverText
              customClassName="subtitle"
              text={accountType}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {displayARSLayout && !!cbu && (
          <div className="informationField">
            <div className="title">CBU</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(cbu))}>
              <NudosHoverText customClassName="subtitle" text={cbu} charactersLimit={charactersLimit} onlyIfTruncated />
              <CopyIcon className="copyTextIcon" stroke="#383838" />
            </div>
          </div>
        )}
        {!!accountHolderName && (
          <div className="informationField">
            <div className="title">Nombre del titular de la cuenta</div>
            <NudosHoverText
              customClassName="subtitle"
              text={accountHolderName}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {!!beneficiaryBank && (
          <div className="informationField">
            <div className="title">Banco beneficiario</div>
            <NudosHoverText
              customClassName="subtitle"
              text={beneficiaryBank}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {!!beneficiaryAddress && (
          <div className="informationField">
            <div className="title">Dirección del beneficiario</div>
            <NudosHoverText
              customClassName="subtitle"
              text={beneficiaryAddress}
              charactersLimit={2 * charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {showOthersBankDataPeru && (
          <div className="informationField">
            <div className="title">{'Otros datos bancarios (ej. Swift)'}</div>
            <NudosHoverText
              customClassName="subtitle"
              text={displaySwift}
              charactersLimit={charactersLimit}
              onlyIfTruncated
            />
          </div>
        )}
        {displayARSLayout && !!cuit && (
          <div className="informationField">
            <div className="title">CUIT</div>
            <div className="accountCointainer" onClick={() => navigator.clipboard.writeText(String(cuit))}>
              <NudosHoverText
                customClassName="subtitle"
                text={cuit}
                charactersLimit={charactersLimit}
                onlyIfTruncated
              />
              <CopyIcon className="copyTextIcon" stroke="#383838" />
            </div>
          </div>
        )}
        {!manualQuote && !(transferIsARS || transerIsBRL) && (
          <div className="informationField">
            <div className="title">Valor a transferir</div>
            <NudosHoverText
              customClassName="subtitle"
              charactersLimit={charactersLimit}
              onlyIfTruncated
              text={transferValueText}
            />
          </div>
        )}
      </div>
      <NudosButton
        customClassName="closeModalButton"
        buttonText="Entendido"
        componentSize="large"
        handleClick={closeModalCallback}
      />
    </div>
  );
};

export default PaymentDetailsModalContent;
